import React, { useEffect, useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import debounce from 'lodash.debounce'

import InfoBox from 'components/Shared/InfoBox'
import { useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import DropDown from 'components/Shared/DropDown'

const MIN_AGE = 18
const DEFAULT_MIN_AGE = 18
const MAX_AGE = 65

export default function AgeRange({ value, onChange }) {
  const { t } = useTranslation()

  const { loading: setupLoading } = useCampaignSetup()
  const [inputValues, setInputValues] = useState({
    min: value?.min ?? DEFAULT_MIN_AGE,
    max: value?.max ?? MAX_AGE,
  })

  useEffect(() => {
    setInputValues({
      min: value?.min ?? DEFAULT_MIN_AGE,
      max: value?.max ?? MAX_AGE,
    })
  }, [value])

  const debouncedOnChange = useCallback(
    debounce((newMin, newMax) => {
      onChange({ min: newMin, max: newMax })
    }, 500),
    [onChange],
  )

  const handleChange = (value, name) => {
    debouncedOnChange.cancel()

    const parsed = parseInt(value, 10)

    if (isNaN(parsed)) {
      setInputValues((prev) => ({ ...prev, [name]: '' }))
      return
    }

    let newState = { ...inputValues, [name]: parsed }

    // Keep min ≤ max
    if (name === 'min' && parsed > (inputValues.max || MAX_AGE)) {
      newState.max = parsed
    } else if (name === 'max' && parsed < (inputValues.min || MIN_AGE)) {
      newState.min = parsed
    }

    setInputValues(newState)

    // Clamp values before sending
    const finalMin = Math.max(MIN_AGE, Math.min(newState.min, MAX_AGE))
    const finalMax = Math.max(MIN_AGE, Math.min(newState.max, MAX_AGE))

    debouncedOnChange(finalMin, finalMax)
  }

  const ageOptions = useMemo(
    () =>
      Array.from({ length: MAX_AGE - MIN_AGE + 1 }, (_, i) => ({
        value: MIN_AGE + i,
        text: MIN_AGE + i === 65 ? '65+' : `${MIN_AGE + i}`,
      })),
    [],
  )

  return (
    <div className="age-range form-group mb-0">
      <InfoBox content="Tips.Recommendations.AgeRange">
        <label className="d-inline-flex mb-3" htmlFor="ageRangeMin">
          {t('Recommendations.summary.advancedSettings.ageRange.title')}
        </label>
      </InfoBox>
      <div className="d-flex gap-5">
        <div className="form-group input-cell input-cell--input d-flex gap-2 align-items-center">
          <label htmlFor="ageRangeMin" className="mb-0">
            {t('Recommendations.summary.advancedSettings.ageRange.min')}
          </label>
          <DropDown
            classNames="age-range__min"
            options={ageOptions}
            placeholder={t('Recommendations.summary.advancedSettings.ageRange.minPlaceholder')}
            required={false}
            onChange={(value) => handleChange(value, 'min')}
            value={inputValues.min}
            disabled={setupLoading}
            name="min"
            placementType="top"
            forcePlacement
            showArrowWhenDisabled
          />
        </div>
        <div
          className="form-group input-cell input-cell--input d-flex gap-2 align-items-center">
          <label htmlFor="ageRangeMax" className="mb-0">
            {t('Recommendations.summary.advancedSettings.ageRange.max')}
          </label>
          <DropDown
            classNames="age-range__max"
            options={ageOptions}
            placeholder={t('Recommendations.summary.advancedSettings.ageRange.maxPlaceholder')}
            required={false}
            onChange={(value) => handleChange(value, 'max')}
            value={inputValues.max}
            disabled={setupLoading}
            name="max"
            placementType="top"
            forcePlacement
            showArrowWhenDisabled
          />
        </div>
      </div>
    </div>
  )
}

AgeRange.propTypes = {
  value: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
}
